import { createSelector } from "reselect";
import moment from "moment";
import "moment/locale/es";
import { ROLES_RAW } from "../../constants/permissions";
import { routes } from "../../constants/routes";
moment().locale("es");

const DEFAULT_NAV_PRODUCTS_PREFIX = "MP";
const POSSIBLE_NUMBER_KEYS = [
  "No",
  "No_",
  "no",
  "numero",
  "Numero",
  "number",
  "Number",
  "code",
  "Code",
  "codigo",
  "Codigo",
  "productNumber",
  "ProductNumber",
  "id",
  "ID",
];
const POSSIBLE_DESCRIPTION_KEYS = [
  "Description",
  "description",
  "Description2",
  "description2",
  "descripcion",
  "Descripcion",
  "descripcion2",
  "Descripcion2",
  "name",
  "Name",
  "nombre",
  "Nombre",
  "productName",
  "ProductName",
];

const normalizeString = (value) =>
  typeof value === "string" ? value.trim() : "";

const startsWithNavPrefix = (value, prefix = DEFAULT_NAV_PRODUCTS_PREFIX) => {
  const normalizedPrefix =
    typeof prefix === "string" ? prefix.trim().toUpperCase() : "";

  return (
    !!value &&
    !!normalizedPrefix &&
    normalizeString(value).toUpperCase().startsWith(normalizedPrefix)
  );
};

const parseNavProductString = (value, prefix = DEFAULT_NAV_PRODUCTS_PREFIX) => {
  const text = normalizeString(value);
  if (!text) {
    return null;
  }

  const parts = text.split(/[-–—\s]+/).filter(Boolean);
  if (!parts.length) {
    return null;
  }

  const candidate = parts[0];
  if (!startsWithNavPrefix(candidate, prefix)) {
    return null;
  }

  const description = normalizeString(
    text
      .slice(text.indexOf(candidate) + candidate.length)
      .replace(/^[\s-–—:,]+/, "")
  );

  return {
    number: candidate,
    description,
  };
};

const extractNavProductNumberFromObject = (
  item,
  prefix = DEFAULT_NAV_PRODUCTS_PREFIX
) => {
  if (!item || typeof item !== "object") {
    return null;
  }

  for (const key of POSSIBLE_NUMBER_KEYS) {
    if (Object.prototype.hasOwnProperty.call(item, key)) {
      const value = normalizeString(item[key]);
      if (startsWithNavPrefix(value, prefix)) {
        return value;
      }
    }
  }

  for (const value of Object.values(item)) {
    const candidate = normalizeString(value);
    if (startsWithNavPrefix(candidate, prefix)) {
      return candidate;
    }
  }

  return null;
};

const extractNavProductDescriptionFromObject = (
  item,
  number,
  prefix = DEFAULT_NAV_PRODUCTS_PREFIX
) => {
  if (!item || typeof item !== "object") {
    return "";
  }

  const numberUpper = normalizeString(number).toUpperCase();

  for (const key of POSSIBLE_DESCRIPTION_KEYS) {
    if (Object.prototype.hasOwnProperty.call(item, key)) {
      const value = normalizeString(item[key]);
      if (value && value.toUpperCase() !== numberUpper) {
        return value;
      }
    }
  }

  for (const value of Object.values(item)) {
    const candidate = normalizeString(value);
    if (
      candidate &&
      candidate.toUpperCase() !== numberUpper &&
      !startsWithNavPrefix(candidate, prefix)
    ) {
      return candidate;
    }
  }

  return "";
};

const buildNavProductOption = (
  number,
  description,
  raw,
  prefix = DEFAULT_NAV_PRODUCTS_PREFIX
) => {
  const normalizedNumber = normalizeString(number);
  if (!startsWithNavPrefix(normalizedNumber, prefix)) {
    return null;
  }

  const normalizedDescription = normalizeString(description);
  const label = normalizedDescription
    ? `${normalizedNumber} - ${normalizedDescription}`
    : normalizedNumber;

  return {
    label,
    value: label,
    number: normalizedNumber,
    description: normalizedDescription,
    raw,
  };
};

/* APP */
export const isUserAuthorized = createSelector(
  (state) => state.authorization,
  (authorization) => authorization
);

const resolveRouteFromKey = (routeKey) => {
  if (!routeKey) {
    return null;
  }
  return routes[routeKey] || null;
};

export const getCurrentRouteKey = createSelector(
  (state) => state.currentRoute,
  (currentRoute) => currentRoute
);
export const getCurrentRoute = createSelector(
  getCurrentRouteKey,
  (routeKey) => resolveRouteFromKey(routeKey)
);
export const getCurrentRouteTitle = createSelector(
  getCurrentRoute,
  (route) => route?.title
);
export const getSideMenuState = createSelector(
  (state) => state.app,
  (app) => app.isCollapsedSideMenu
);
export const getCurrentTheme = createSelector(
  (state) => state.currentTheme,
  (currentTheme) => currentTheme
);

/* PROFILE */
export const getUserProfile = createSelector(
  (state) => state.profile,
  (profile) => profile
);
export const currentUserId = createSelector(
  (state) => state.profile,
  (profile) => profile && profile.id
);
export const getCurrentUserRole = createSelector(getUserProfile, (profile) =>
  !profile ? null : ROLES_RAW.includes(profile.role) ? profile.role : null
);

/* NOTIFICATIONS STATE */
export const getNotificationsState = createSelector(
  (state) => state.notifications,
  (notifications) => notifications
);

/* USERS */
export const getUsers = createSelector(
  (state) => state.users,
  (users) => users
);
export const getUsersForm = createSelector(
  (state) => state.usersForm,
  (form) => form
);

/* ALERT */
export const getAlerts = createSelector(
  (state) => state.alerts,
  (alerts) => alerts
);
export const getAlertsForm = createSelector(
  (state) => state.alertsForm,
  (form) => form
);

/* RAWMATERIALS */
export const getRawmaterials = createSelector(
  (state) => state.rawmaterials,
  (rawmaterials) => rawmaterials
);
export const getRawmaterialsForm = createSelector(
  (state) => state.rawmaterialsForm,
  (form) => form
);

/* ZONES */
export const getZones = createSelector(
  (state) => state.zones,
  (zones) => zones
);
export const getZonesForm = createSelector(
  (state) => state.zonesForm,
  (form) => form
);

/* MACHINES */
export const getMachines = createSelector(
  (state) => state.machines,
  (machines) => machines
);
export const getMachinesForm = createSelector(
  (state) => state.machinesForm,
  (form) => form
);
export const getMachineById = (id) =>
  createSelector(
    (state) => state.machines,
    (machines) =>
      machines
        ? Object.values(machines).find((machine) => machine._id === id)
        : null
  );
export const getRawmaterialById = (id) =>
  createSelector(
    (state) => state.rawmaterials,
    (rawmaterials) =>
      rawmaterials
        ? Object.values(rawmaterials).find(
            (rawmaterial) => rawmaterial._id === id
          )
        : null
  );

/* REPCON */
export const getRepcon = createSelector(
  (state) => state.repcon,
  (repcon) => repcon
);

const buildNavProductsSelector = (prefix = DEFAULT_NAV_PRODUCTS_PREFIX) =>
  createSelector(
    (state) => state.navProducts,
    (navProducts) => {
      if (!navProducts) {
        return [];
      }

      const items = Array.isArray(navProducts) ? navProducts : [navProducts];

      const options = items
        .map((item) => {
          if (!item) {
            return null;
          }

          if (typeof item === "string") {
            const parsed = parseNavProductString(item, prefix);
            if (!parsed) {
              return null;
            }
            return buildNavProductOption(
              parsed.number,
              parsed.description,
              item,
              prefix
            );
          }

          if (typeof item === "object") {
            const number = extractNavProductNumberFromObject(item, prefix);
            if (!number) {
              return null;
            }
            const description = extractNavProductDescriptionFromObject(
              item,
              number,
              prefix
            );
            return buildNavProductOption(number, description, item, prefix);
          }

          return null;
        })
        .filter(Boolean);

      return options.sort((a, b) =>
        a.label.localeCompare(b.label, "es", { sensitivity: "base" })
      );
    }
  );

/* NAVPRODUCTS */
export const getNavProducts = buildNavProductsSelector("MP");
export const getNavProductsPF = buildNavProductsSelector("PF");

/* FINISHEDPRODUCTS */
export const getFinishedproducts = createSelector(
  (state) => state.finishedproducts,
  (finishedproducts) => finishedproducts
);
export const getFinishedproductsForm = createSelector(
  (state) => state.finishedproductsForm,
  (form) => form
);
export const getFinishedproductById = (id) =>
  createSelector(
    (state) => state.finishedproducts,
    (finishedproducts) =>
      finishedproducts
        ? Object.values(finishedproducts).find(
            (finishedproduct) => finishedproduct._id === id
          )
        : null
  );

/* CLIENTS */
export const getClients = createSelector(
  (state) => state.clients,
  (clients) => clients
);
export const getClientsForm = createSelector(
  (state) => state.clientsForm,
  (form) => form
);
export const getClientById = (id) =>
  createSelector(
    (state) => state.clients,
    (clients) => (clients && id ? clients[id] : null)
  );

/* CLIENTPRODUCTS */
export const getClientproducts = createSelector(
  (state) => state.clientproducts,
  (clientproducts) => clientproducts
);
export const getClientproductsForm = createSelector(
  (state) => state.clientproductsForm,
  (form) => form
);
export const getClientproductById = (id) =>
  createSelector(
    (state) => state.clientproducts,
    (clientproducts) =>
      clientproducts
        ? Object.values(clientproducts).find(
            (clientproduct) => clientproduct._id === id
          )
        : null
  );
